import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Text } from 'foundations-components/components/Text';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UITable from 'UIComponents/table/UITable';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITableActionsRow from 'UIComponents/table/UITableActionsRow';
import { Flex } from 'foundations-components/components/Flex';
import { Link } from 'foundations-components/transitional/components/Link';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { getDuplicateForecastCategoryLabels, selectedCategoriesAreUsed } from '../../../utils/wizardUtils';
import { trackWizardInteraction } from '../../../utils/usageTracker';
import { WIZARD_FORECAST_CATEGORY } from '../../../constants/WizardStepTypes';
import { DEFAULT_CATEGORIES_NOT_EDITABLE } from '../../../constants/ForecastCategories';
import Links from '../../../constants/Links';
import { DELETE_CATEGORY_ONBOARDING, DELETE_IS_BLOCKED_ONBOARDING, MAX_CATEGORIES_ONBOARDING } from '../../../constants/Events';
import { removeForecastCategories, setForecastCategory } from '../../../actions/WizardActions';
import CustomizeForecastCategoriesTableRow from '../table/CustomizeForecastCategoryTableRow';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const blockedDeleteToolTip = /*#__PURE__*/_jsxs(Flex, {
  flexDirection: "column",
  children: [/*#__PURE__*/_jsx(FormattedMessage, {
    message: "wizard.customize.table.deleteBlockedMessage"
  }), /*#__PURE__*/_jsx(List, {
    styled: true,
    marginBlockStart: "300",
    marginBlockEnd: "0",
    children: /*#__PURE__*/_jsx(ListItem, {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "wizard.customize.table.deleteBlockedListItem_jsx",
        options: {
          external: true,
          href: Links.CONDITIONAL_PROPERTIES_LOGIC_FORECAST_CATEGORIES
        },
        elements: {
          Link
        }
      })
    })
  })]
});
const headerRow = (forecastCategories, selectedRows, deleteIsBlocked, setSelectedRows, dispatch) => /*#__PURE__*/_jsxs(UITableActionsRow, {
  open: selectedRows.length > 0,
  openAtColumnIndex: 1,
  actions:
  /*#__PURE__*/
  // TODO: replace UILink with `Button` from foundations-components when the link styling is ready
  // https://git.hubteam.com/HubSpot/forecasting-team/issues/2409
  _jsxs("span", {
    children: [/*#__PURE__*/_jsxs("small", {
      children: [selectedRows.length, " selected"]
    }), /*#__PURE__*/_jsx(UITooltip, {
      title: deleteIsBlocked && blockedDeleteToolTip,
      use: "longform",
      children: /*#__PURE__*/_jsxs(UILink, {
        "data-test-id": "delete-selected-categories",
        disabled: deleteIsBlocked,
        onClick: () => {
          trackWizardInteraction('customize-categories', DELETE_CATEGORY_ONBOARDING);
          dispatch(removeForecastCategories(selectedRows));
          setSelectedRows([]);
        },
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "delete",
          className: "m-left-3"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "wizard.customize.table.bulkActionDelete"
        })]
      })
    })]
  }),
  children: [/*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(Checkbox, {
      "aria-label": "Toggle selection for all rows",
      "data-test-id": "select-all-rows",
      checked: selectedRows.length > 0 && selectedRows.length === forecastCategories.length - 2,
      indeterminate: selectedRows.length > 0,
      onChange: () => {
        if (selectedRows.length === forecastCategories.length - 2) {
          setSelectedRows([]);
        } else {
          setSelectedRows(forecastCategories.map(category => category.value).filter(category => !DEFAULT_CATEGORIES_NOT_EDITABLE.includes(category)));
        }
      }
    })
  }), /*#__PURE__*/_jsx("th", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "wizard.customize.table.forecastCategories.title"
    })
  }), /*#__PURE__*/_jsxs("th", {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "wizard.customize.table.categoryDefinitions.title"
    }), /*#__PURE__*/_jsx(HelpIcon, {
      marginInlineStart: "100",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.customize.table.categoryDefinitions.definitionHelpText"
      })
    })]
  })]
});
const footerRow = (forecastCategoriesAtLimit, getCategoryIndexAndIncrement, dispatch) => /*#__PURE__*/_jsxs("tr", {
  children: [/*#__PURE__*/_jsx("td", {}), /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsx(UITooltip, {
      title: forecastCategoriesAtLimit && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.customize.table.maxCategoryTooltip"
      }),
      children: /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        "data-test-id": "add-category",
        disabled: forecastCategoriesAtLimit,
        onClick: () => {
          dispatch(setForecastCategory(`${WIZARD_FORECAST_CATEGORY}-${getCategoryIndexAndIncrement()}`, '', ''));
        },
        children: [/*#__PURE__*/_jsx(UIIcon, {
          name: "add"
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "wizard.customize.table.addCategoryButton"
        })]
      })
    })
  }), /*#__PURE__*/_jsx("td", {})]
});
const CustomizeForecastCategories = ({
  forecastCategories,
  referencedCategories
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const duplicateForecastCategoryLabels = getDuplicateForecastCategoryLabels(forecastCategories);
  const dispatch = useDispatch();
  const forecastCategoriesAtLimit = forecastCategories.length === 7;
  const deleteIsBlocked = useMemo(() => selectedCategoriesAreUsed(selectedRows, referencedCategories), [selectedRows, referencedCategories]);
  const categoryIndex = useRef(1);
  const getCategoryIndexAndIncrement = () => {
    const index = categoryIndex.current;
    categoryIndex.current++;
    return index;
  };
  useEffect(() => {
    if (forecastCategoriesAtLimit) {
      trackWizardInteraction('customize-categories', MAX_CATEGORIES_ONBOARDING);
    }
  }, [forecastCategoriesAtLimit]);
  useEffect(() => {
    if (deleteIsBlocked) {
      trackWizardInteraction('customize-categories', DELETE_IS_BLOCKED_ONBOARDING);
    }
  }, [deleteIsBlocked]);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(Text, {
      as: "h2",
      marginBlockEnd: "400",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.customize.title"
      })
    }), /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.customize.description"
      })
    }), /*#__PURE__*/_jsxs("p", {
      children: [/*#__PURE__*/_jsx("b", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "wizard.customize.noteTitle"
        })
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "wizard.customize.noteBody"
      })]
    }), /*#__PURE__*/_jsxs(UITable, {
      condensed: true,
      hover: false,
      "data-test-id": "customize-categories-table",
      children: [/*#__PURE__*/_jsxs("colgroup", {
        children: [/*#__PURE__*/_jsx("col", {
          style: {
            width: 60
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '50%'
          }
        }), /*#__PURE__*/_jsx("col", {
          style: {
            width: '50%'
          }
        })]
      }), /*#__PURE__*/_jsx("thead", {
        children: headerRow(forecastCategories, selectedRows, deleteIsBlocked, setSelectedRows, dispatch)
      }), /*#__PURE__*/_jsxs("tbody", {
        children: [forecastCategories.map(category => /*#__PURE__*/_jsx(CustomizeForecastCategoriesTableRow, {
          forecastCategoryKey: category.value,
          forecastCategory: category.label,
          categoryDefinition: category.description,
          selectedRows: selectedRows,
          setSelectedRows: setSelectedRows,
          duplicateForecastCategoryLabels: duplicateForecastCategoryLabels,
          referencedCategories: referencedCategories
        }, category.value)), footerRow(forecastCategoriesAtLimit, getCategoryIndexAndIncrement, dispatch)]
      })]
    })]
  });
};
export default CustomizeForecastCategories;